import moment from 'moment-timezone';
import { isEmpty } from 'lodash-es';
import {
    MomentDateFormat,
    MomentTimeFormat,
    DEFAULT_DATETIME_FORMAT,
} from '../constants/momentFormats';
import { getTimezones } from './l10n';

/** A timezone entry as stored in the bundled timezone tables. */
interface TimezoneEntry {
    id?: string;
    value?: string;
    iana?: string;
    offset?: number;
}

/** Format a date string as `M/D/YYYY` in UTC, or null when falsy. */
export const getShortUTC = (str: moment.MomentInput): string | null => {
    if (!str) return null;

    const d = new Date(str as string);
    const ye = d.getUTCFullYear();
    const mo = d.getUTCMonth() + 1; // offset by 1
    const da = d.getUTCDate();
    return `${mo}/${da}/${ye}`;
};

/** Format a date as `MM/DD/YYYY` (local), or null when falsy. */
export const getShort = (str: moment.MomentInput): string | null => {
    if (!str) return null;
    return moment(str).format('MM/DD/YYYY');
};

/** Format a date as `YYYY-MM-DD` suitable for date inputs, or null when falsy. */
export const getInputFormat = (str: moment.MomentInput): string | null => {
    if (!str) return null;

    const d = new Date(str as string);
    const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(d);
    const mo = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(d);
    const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(d);
    return `${ye}-${mo}-${da}`;
};

/** Return an array of Date objects for every day in the inclusive range. */
export const getDaysInRange = (startDate: Date | string, endDate: Date): Date[] => {
    let dates: Date[] = [];
    // to avoid modifying the original date
    const theDate = new Date(startDate);
    while (theDate <= endDate) {
        dates = [...dates, new Date(theDate)];
        theDate.setDate(theDate.getDate() + 1);
    }
    return dates;
};

/** Return true when the given offset renders a different wall-clock time in the browser zone. */
export const isTimeDifferentInBrowserZone = (
    dateTimeOffset: moment.MomentInput,
    ianaZone: string,
): boolean => {
    const m = moment.parseZone(dateTimeOffset);
    const inProvidedZone = m.clone().tz(ianaZone);
    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const inBrowserZone = m.clone().tz(browserZone);

    try {
        return inProvidedZone.format('HH:mm') !== inBrowserZone.format('HH:mm');
    } catch (e) {
        console.error('Unable to detect timezones', { error: e });
        return false;
    }
};

/** Convert a Windows timezone id/value to its IANA value. */
export const convertWinToIANA = (winTimeZone: string): string | undefined => {
    const { ianaTimezones } = getTimezones();
    return (ianaTimezones as TimezoneEntry[])?.find(
        (tz) => tz.id === winTimeZone || tz.value === winTimeZone,
    )?.value;
};

/** Resolve an ISO datetime string and IANA zone from an offset or a date/time pair. */
export const getTimezone = (
    offset: string | undefined,
    winTimeZone: string,
    date?: string | Date,
    time?: string,
): { dateTimeISOString: string | undefined; ianaTimezone: string | undefined } => {
    const ianaTimezone = convertWinToIANA(winTimeZone);
    let dateTimeISOString: string | undefined = offset;

    if (!offset && time?.length && ianaTimezone?.length) {
        const match = time.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);

        if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const dt = !isEmpty(date) ? new Date(date as string) : new Date();
            dt.setHours(hours, minutes, 0, 0);
            dateTimeISOString = moment.tz(dt, ianaTimezone).toISOString();
        } else {
            const startTimeDate = new Date(time);
            const hours = startTimeDate.getHours();
            const minutes = startTimeDate.getMinutes();
            const dt = !isEmpty(date) ? new Date(date as string) : new Date();
            dt.setHours(hours, minutes, 0, 0);
            dateTimeISOString = moment.tz(dt, ianaTimezone).toISOString();
        }
    }

    return { dateTimeISOString, ianaTimezone };
};

/** Parse the signed offset (in minutes) from the trailing `+HH:mm`/`-HH:mm` of an ISO string. */
export const getOffsetMinutesFromISOString = (dateTimeOffsetString: string): number => {
    const match = dateTimeOffsetString.match(/([+-])(\d{2}):(\d{2})$/);
    if (!match) return 0;

    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);

    return sign * ((hours * 60) + minutes);
};

/** Resolve a timezone abbreviation from an ISO datetime string and (optional) IANA zone. */
export const timezoneAbbr = (
    datetimeoffset?: string,
    ianaTimezone?: string,
    timeZoneName: 'short' | 'long' = 'short',
    forceShow = false,
): string => {
    if (!datetimeoffset || datetimeoffset?.length < 1) {
        return '';
    }

    let resolvedZone = ianaTimezone;

    if (!resolvedZone) {
        const offsetMinutes = getOffsetMinutesFromISOString(datetimeoffset);
        if (offsetMinutes === 0) {
            return 'UTC';
        }
        const timezones = getTimezones();
        const ianaTimezones = (timezones.ianaAproximateByOffset as TimezoneEntry[]).filter(
            (z) => z.offset === offsetMinutes,
        );

        if (ianaTimezones.length > 0) {
            resolvedZone = ianaTimezones.find((tz) => {
                const dt = moment.tz(datetimeoffset, tz.iana as string);
                if (dt.utcOffset() === tz.offset) {
                    return true;
                }
                return undefined;
            })?.iana;
        }

        if (!resolvedZone) {
            console.info('No iana zone found');
            return '';
        }
    }

    if (forceShow || isTimeDifferentInBrowserZone(datetimeoffset, resolvedZone)) {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: resolvedZone,
            timeZoneName,
        });

        const parts = formatter.formatToParts(new Date(datetimeoffset ?? Date.now()));
        const tzPart = parts.find((part) => part.type === 'timeZoneName');
        return tzPart ? tzPart.value : '';
    }
    return '';
};

/** Resolve a timezone abbreviation (e.g. `EST`) from an offset/Windows zone. */
export const getTimeZoneAbbr = (
    offset: string | undefined,
    winTimeZone: string,
    date?: string | Date,
    time?: string,
    forceShow = false,
): string => {
    const { dateTimeISOString, ianaTimezone } = getTimezone(offset, winTimeZone, date, time);
    return timezoneAbbr(dateTimeISOString, ianaTimezone, 'short', forceShow);
};

/** Append a timezone abbreviation in parentheses to `text`, when one can be resolved. */
export const appendTimeZone = (
    text: string,
    offset: string | undefined,
    winTimeZone: string,
    date?: string | Date,
    time?: string,
    forceShow = false,
): string => {
    const tzAbbr = getTimeZoneAbbr(offset, winTimeZone, date, time, forceShow);
    return tzAbbr?.length ? `${text} (${tzAbbr})` : text;
};

export const appendDispatchTimeZone = (
    text: string,
    offset: string | undefined,
    jobWinTimeZone: string,
    dispatchWinTimeZone: string | undefined,
    dispatchFormat: string,
): string => {
    const dispatchDiffersFromJob = !!dispatchWinTimeZone && dispatchWinTimeZone !== jobWinTimeZone;

    if (!dispatchDiffersFromJob) {
        return appendTimeZone(text, offset, jobWinTimeZone);
    }

    const jobAbbr = getTimeZoneAbbr(offset, jobWinTimeZone, undefined, undefined, true);
    const primary = jobAbbr?.length ? `${text} ${jobAbbr}` : text;

    const dispatchIana = convertWinToIANA(dispatchWinTimeZone);
    if (!offset || !dispatchIana) {
        return primary;
    }

    const dispatchTime = moment.parseZone(offset).clone().tz(dispatchIana).locale('en').format(dispatchFormat);
    const dispatchAbbr = timezoneAbbr(offset, dispatchIana, 'short', true);

    return dispatchAbbr?.length ? `${primary} (${dispatchTime} ${dispatchAbbr})` : primary;
};

/** Format an offset (or fallback) with the given format and append its timezone abbreviation. */
export const formatOffset = (
    offset: string | undefined,
    format: string,
    timezone: string,
    fallback: string,
): string => {
    let dateTime = fallback;
    if (offset) {
        dateTime = offset;
    }
    const dt = moment.parseZone(dateTime).format(format);
    return appendTimeZone(dt, offset, timezone, fallback);
};

/** Convert a UTC datetime string to a local `DateTimeShortAnte`-formatted string. */
export const getDateTimeFromUtc = (dateStringUTC: moment.MomentInput): string => {
    const dateUtc = moment.utc(dateStringUTC).toDate();
    return moment(dateUtc).local().format(MomentDateFormat.DateTimeShortAnte);
};

/** Parse a 24-hour time string (`HH:mm`) into a moment. */
export const formatTime24Hr = (stringNumber: string): moment.Moment => moment(
    stringNumber,
    MomentTimeFormat.TwentyFourHourWithLeading,
);

/** Format a 24-hour time (`H:mm` or `H:mm:ss`) as `h:mm A`. */
export const formatTime12Hour = (
    value: string | null | undefined,
): string => {
    if (!value) return '';

    const time = moment(
        value,
        ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'H:mm'],
        true,
    );

    return time.isValid()
        ? time.format(MomentTimeFormat.AnteTwelveHour)
        : value;
};

/** Day the week starts on when a tenant has no setting: Monday (0 = Sunday ... 6 = Saturday, like .NET `DayOfWeek`). */
export const defaultWeekStartsOn = 1;
export const billingWeekStartsOn = 1;

/** Number of days from the week start to `date` (0 when `date` is the start day), regardless of the moment locale. */
export const getDaysSinceWeekStart = (
    date: moment.MomentInput,
    weekStartsOn: number = defaultWeekStartsOn,
): number => (moment(date).day() - weekStartsOn + 7) % 7;

/**
 * First day (local midnight) of the tenant week that contains `date`.
 * `weekStartsOn` is the tenant "Week starts on" value (0 = Sunday ... 6 = Saturday). Fixed `.day()` arithmetic,
 * so the result never depends on the active moment locale.
 */
export const getWeekStartMoment = (
    date: moment.MomentInput,
    weekStartsOn: number = defaultWeekStartsOn,
): moment.Moment => moment(date)
    .subtract(getDaysSinceWeekStart(date, weekStartsOn), 'days')
    .startOf('day');

/** Last moment (23:59:59.999) of the tenant week that contains `date`: the week start plus 6 days. */
export const getWeekEndMoment = (
    date: moment.MomentInput,
    weekStartsOn: number = defaultWeekStartsOn,
): moment.Moment => getWeekStartMoment(date, weekStartsOn).add(6, 'days').endOf('day');

/** Return the first day of the tenant week containing `date`, formatted (default `YYYY-MM-DD`). */
export const getWeekStartDate = (
    date: moment.MomentInput,
    weekStartsOn: number = defaultWeekStartsOn,
    format = 'YYYY-MM-DD',
): string => getWeekStartMoment(date, weekStartsOn).format(format);

/** Return the last day of the tenant week containing `date` (week start plus 6 days), formatted (default `YYYY-MM-DD`). */
export const getWeekEndDate = (
    date: moment.MomentInput,
    weekStartsOn: number = defaultWeekStartsOn,
    format = 'YYYY-MM-DD',
): string => getWeekEndMoment(date, weekStartsOn).format(format);

/**
 * Read the tenant "Week starts on" setting (0 = Sunday ... 6 = Saturday) from the tenant settings,
 * falling back to Monday when it is absent or not a valid day number.
 */
export const resolveWeekStartsOn = (
    tenantSettings?: Record<string, unknown> | null,
): number => {
    const raw = tenantSettings?.payPeriodBeginDayOfWeek;
    const value = raw === null || raw === undefined || raw === '' ? Number.NaN : Number(raw);

    return Number.isInteger(value) && value >= 0 && value <= 6 ? value : defaultWeekStartsOn;
};

/**
 * The first day of the week (0 = Sunday ... 6 = Saturday) that the ACTIVE moment locale defines: Sunday for
 * English, Monday for Spanish. This is the user's language, not the tenant's "Week starts on". Only the calendar
 * grids (Job Calendar, My Calendar, the Project Manager scheduler) read it: their first column follows the language,
 * as it always has, while every week boundary that reaches the server comes from the tenant setting.
 */
export const getLocaleWeekStartsOn = (): number => moment.localeData().firstDayOfWeek();

/** Return the lowercase day name (e.g. `monday`) for a date. */
export const getDayName = (date: moment.MomentInput): string => moment(date).format('dddd').toLowerCase();

/** Return the duration in hours between two `HH:mm` times, rolling to the next day when needed. */
export const getTotalHours = (start: string, end: string): string | undefined => {
    // Calculate the duration between Start and End. Factor in if this enters into the next day.
    // Use the job start date/time as opposed to moment defaulting to the current day.
    if (start && end) {
        const timeFormat = 'HH:mm';

        const mStartTime = moment(start, timeFormat);
        const mEndTime = moment(end, timeFormat);
        if (mEndTime.isBefore(mStartTime)) {
            mEndTime.add(1, 'day');
        }

        const dur = moment.duration(mEndTime.diff(mStartTime));
        return dur.asHours().toFixed(2);
    }
    return undefined;
};

/**
 * Re-anchor a date's wall clock (the calendar date and HH:mm the browser shows for it) into the
 * given Windows-id or IANA timezone and format it as an ISO offset string — the instant the API
 * stores. The date and time are kept exactly as typed; only the offset changes, so a dispatcher
 * in any timezone saves the time the job site reads. With no resolvable zone the wall clock is
 * kept in the browser's own offset.
 */
export const convertTimezone = (date: moment.MomentInput, timeZone?: string): string => {
    const wallClock = moment(date).format(DEFAULT_DATETIME_FORMAT);
    const targetZone = timeZone ? convertWinToIANA(timeZone) : undefined;
    const anchored = targetZone
        ? moment.tz(wallClock, DEFAULT_DATETIME_FORMAT, targetZone)
        : moment(wallClock, DEFAULT_DATETIME_FORMAT);

    return anchored.seconds(0).milliseconds(0).format();
};

/** The fields of a job that decide the zone its start time is saved in. */
interface ZonedJob {
    jobLocation?: { timeZone?: string | null } | null;
    dispatchOfficeLocationId?: number | string | null;
}

/** A dispatch-office typeahead option, which carries the office location's zone. */
interface DispatchLocationOption {
    value: number | string;
    timeZone?: string | null;
}

/**
 * The zone a job's start time is saved in: the job location's zone, else the zone of its dispatch
 * office (from the dispatch-office typeahead options, which already fall back to the tenant's).
 * Mirrors the server's fallback ladder so the client converts into the zone the server will label
 * the instant with. Undefined when neither is known, and convertTimezone() then keeps the
 * browser offset.
 */
export const resolveJobTimeZone = (
    job: ZonedJob,
    dispatchLocations?: DispatchLocationOption[] | null,
): string | undefined => job.jobLocation?.timeZone
    || dispatchLocations?.find((d) => d.value === job.dispatchOfficeLocationId)?.timeZone
    || undefined;

/** Format an ISO offset string in a resolved timezone, appending the abbreviation when it differs. */
export const formatOffsetInTimeZone = (
    dateTimeOffsetString: string | undefined,
    format: string,
    winTimezone?: string,
): string => {
    if (!dateTimeOffsetString || !format) {
        return '';
    }

    let ianaTimezone: string | undefined;

    if (winTimezone) {
        ianaTimezone = convertWinToIANA(winTimezone);
    }

    if (!ianaTimezone) {
        const offsetMinutes = getOffsetMinutesFromISOString(dateTimeOffsetString);
        if (offsetMinutes !== 0) {
            const timezones = getTimezones();
            const ianaTimezones = (timezones.ianaAproximateByOffset as TimezoneEntry[]).filter(
                (z) => z.offset === offsetMinutes,
            );

            if (ianaTimezones.length > 0) {
                ianaTimezone = ianaTimezones.find((tz) => {
                    const dt = moment.tz(dateTimeOffsetString, tz.iana as string);
                    if (dt.utcOffset() === tz.offset) {
                        return true;
                    }
                    return undefined;
                })?.iana;
            }
        }

        if (!ianaTimezone) {
            return moment.parseZone(dateTimeOffsetString).format(format);
        }
    }

    const m = moment.parseZone(dateTimeOffsetString);
    const inTargetZone = m.clone().tz(ianaTimezone);
    const formattedTime = inTargetZone.format(format);

    if (isTimeDifferentInBrowserZone(dateTimeOffsetString, ianaTimezone)) {
        const tzAbbr = timezoneAbbr(dateTimeOffsetString, ianaTimezone);
        return tzAbbr ? `${formattedTime} (${tzAbbr})` : formattedTime;
    }

    return formattedTime;
};
